/**
 * Fiche application : cycle de vie complet après le premier déploiement (§3.5).
 *
 * Tout ce qu'un utilisateur peut vouloir faire d'une application déjà en ligne
 * tient sur cet écran : voir son état, lire l'historique de ses versions,
 * pousser une nouvelle version, comparer deux versions ligne à ligne et
 * restaurer une version antérieure en un clic.
 *
 * Les versions viennent de `GET /api/apps/:id/versions` (du plus récent au plus
 * ancien) et non de l'historique des déploiements : une version est la notion
 * métier, un déploiement n'en est que la fabrication. Les tentatives échouées y
 * figurent malgré tout, sans quoi un numéro manquant resterait inexpliqué.
 */

import type { AppVersionDto } from '@portail/shared';
import { Activity, Database, ExternalLink, Lock, Pencil, Sparkles } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getApp, listAppVersions, rollbackApp } from '../api';
import { ModifyForm } from '../components/ModifyForm';
import { RedeployForm } from '../components/RedeployForm';
import { AppStatusBadge } from '../components/status';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  LinkButton,
  PageHeader,
  Skeleton,
} from '../components/ui';
import { VersionDiffPanel } from '../components/VersionDiffPanel';
import {
  nextVersionNumber,
  RollbackConfirmDialog,
  VersionHistory,
} from '../components/VersionHistory';
import { messageOf, useAsync } from '../hooks/useAsync';
import { formatDateTime, formatNumber } from '../lib/format';

/** Intitulé et valeur d'une donnée de l'en-tête. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900 dark:text-slate-100">{children}</dd>
    </div>
  );
}

/** Titre de section, repris à l'identique sur toute la fiche. */
function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {children}
      </h2>
      {hint ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
    </div>
  );
}

/** Fiche utilisateur d'une application. */
export function MyAppDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const app = useAsync(() => getApp(id), [id]);
  const versionsQuery = useAsync(() => listAppVersions(id), [id]);

  const [updateOpen, setUpdateOpen] = useState(false);
  /**
   * Voie de mise à jour. La description est le défaut : c'est celle qui n'exige ni
   * de lire le code ni de retrouver l'artifact d'origine.
   */
  const [mode, setMode] = useState<'describe' | 'paste'>('describe');
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<AppVersionDto | null>(null);
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  // Référence stable : le comparateur s'en sert comme dépendance de chargement.
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);

  // Par défaut, le comparateur porte sur la version en ligne ; à défaut, sur la
  // plus récente — une application dont le dernier déploiement a échoué n'a pas
  // de version courante en tête de liste.
  const defaultVersionId = versions.find((version) => version.isCurrent)?.id ?? versions[0]?.id;
  const targetVersionId = selectedVersionId ?? defaultVersionId ?? null;

  /** Lance la restauration et bascule sur l'écran de progression. */
  const confirmRollback = (): void => {
    if (!rollbackTarget) return;
    setRollbackBusy(true);
    setRollbackError(null);

    rollbackApp(id, rollbackTarget.id)
      .then((response) => navigate(`/deploiements/${response.jobId}`))
      .catch((error: unknown) => {
        setRollbackError(messageOf(error));
        setRollbackBusy(false);
      });
  };

  if (app.loading) {
    return (
      <>
        <PageHeader title="Application" />
        <Card className="space-y-3 p-6">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      </>
    );
  }

  if (app.error || !app.data) {
    return (
      <>
        <PageHeader title="Application introuvable" />
        {app.error ? (
          <ErrorBanner message={app.error} onRetry={app.reload} />
        ) : (
          <EmptyState
            title="Cette application n'existe pas ou ne vous appartient pas"
            description="Elle a peut-être été supprimée. Retournez à la liste pour voir vos applications actuelles."
            action={
              <Link
                to="/mes-applications"
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
              >
                Mes applications
              </Link>
            }
          />
        )}
      </>
    );
  }

  const data = app.data;

  return (
    <>
      <PageHeader
        title={data.slug}
        description={
          <a
            href={data.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 break-all font-mono text-indigo-700 underline underline-offset-2 dark:text-indigo-300"
          >
            {data.url}
            <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </a>
        }
        actions={
          <>
            {/* Déploiement en cours : le seul chemin vers son écran de
                progression, une fois celui-ci quitté. */}
            {data.activeDeploymentId ? (
              <LinkButton to={`/deploiements/${data.activeDeploymentId}`} variant="primary">
                <Activity className="h-4 w-4" aria-hidden="true" />
                Suivre le déploiement en cours
              </LinkButton>
            ) : null}
            <LinkButton to="/mes-applications">Retour à la liste</LinkButton>
            <Button variant="primary" onClick={() => setUpdateOpen((value) => !value)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              {updateOpen ? 'Masquer le formulaire' : 'Mettre à jour'}
            </Button>
          </>
        }
      />

      <div className="space-y-6">
        {/*
          Décision du service informatique sur une demande de suppression. En tête
          de fiche parce que c'est la réponse à une question que l'auteur a posée :
          la reléguer plus bas revient à ne pas répondre.
        */}
        {data.deletionRequestedAt ? (
          <Card className="border-amber-300 p-4 dark:border-amber-800">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Votre demande de suppression est en attente
            </p>
            <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
              Elle a été transmise au service informatique le{' '}
              {formatDateTime(data.deletionRequestedAt)}. L’application reste en ligne d’ici là.
            </p>
          </Card>
        ) : data.deletionRefusedAt ? (
          <Card className="border-red-300 p-4 dark:border-red-800">
            <p className="text-sm font-semibold text-red-900 dark:text-red-200">
              Votre demande de suppression a été refusée
            </p>
            <p className="mt-1 text-sm text-red-800 dark:text-red-300">
              Le {formatDateTime(data.deletionRefusedAt)}
              {data.deletionRefusalReason ? (
                <>
                  , pour ce motif :{' '}
                  <span className="whitespace-pre-line italic">{data.deletionRefusalReason}</span>
                </>
              ) : (
                '.'
              )}
            </p>
          </Card>
        ) : null}

        <Card className="p-5">
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Fact label="Statut">
              <AppStatusBadge status={data.status} />
            </Fact>
            <Fact label="Version en ligne">
              {data.currentVersionNumber !== null ? (
                <span className="font-semibold">v{data.currentVersionNumber} en ligne</span>
              ) : (
                <span className="text-slate-500 dark:text-slate-400">Aucune version en ligne</span>
              )}
              {typeof data.versionCount === 'number' ? (
                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                  ({formatNumber(data.versionCount)} version
                  {data.versionCount > 1 ? 's' : ''} au total)
                </span>
              ) : null}
            </Fact>
            <Fact label="Dernier déploiement">{formatDateTime(data.lastDeployedAt)}</Fact>
            <Fact label="Visites (7 jours)">
              {data.visits7d === null ? (
                <span className="text-slate-500 dark:text-slate-400">
                  Statistiques indisponibles
                </span>
              ) : (
                formatNumber(data.visits7d)
              )}
            </Fact>
          </dl>

          <dl className="mt-4 grid gap-4 border-t border-slate-200 pt-4 sm:grid-cols-2 lg:grid-cols-4 dark:border-slate-800">
            <Fact label="Domaine">
              <span className="font-mono text-xs">{data.domain}</span>
            </Fact>
            <Fact label="Créée le">{formatDateTime(data.createdAt)}</Fact>
            <Fact label="Mémoire allouée">{data.memoryLimitMb} Mo</Fact>
            <Fact label="Données">
              {data.needsDatabase ? (
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <Database
                      className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400"
                      aria-hidden="true"
                    />
                    Enregistrées en base
                  </span>
                </>
              ) : (
                <span className="text-slate-500 dark:text-slate-400">Aucune donnée conservée</span>
              )}
            </Fact>
            {/*
              L'accès est dit pour lui-même, et non comme une conséquence de la
              persistance : une application sans base peut être réservée, et
              l'auteur doit le savoir avant de diffuser l'adresse.
            */}
            <Fact label="Accès">
              {data.forceSso || data.needsDatabase ? (
                <span className="inline-flex items-center gap-1.5">
                  <Lock
                    className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400"
                    aria-hidden="true"
                  />
                  Réservée aux collaborateurs
                </span>
              ) : (
                <span className="text-slate-500 dark:text-slate-400">Adresse publique</span>
              )}
            </Fact>
            <Fact label="Commentaire">
              <span className="whitespace-pre-line">{data.comment ?? '—'}</span>
            </Fact>
          </dl>
        </Card>

        {updateOpen ? (
          <Card className="p-5">
            <h2 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-50">
              Déployer une nouvelle version
            </h2>

            {/*
              Deux voies, et la description est proposée en premier : recoller un
              artifact suppose de savoir lire le code et de l'avoir encore sous la
              main, ce qui est rarement vrai trois semaines après un déploiement.
            */}
            <div
              className="mb-4 flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Mode de mise à jour"
            >
              <Button
                variant={mode === 'describe' ? 'primary' : 'secondary'}
                aria-pressed={mode === 'describe'}
                onClick={() => setMode('describe')}
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Décrire la modification
              </Button>
              <Button
                variant={mode === 'paste' ? 'primary' : 'secondary'}
                aria-pressed={mode === 'paste'}
                onClick={() => setMode('paste')}
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Coller un nouveau code
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {mode === 'describe'
                  ? 'Expliquez ce qui doit changer, sans toucher au code.'
                  : 'Remplacez le code par une nouvelle version de votre artifact.'}
              </span>
            </div>

            {mode === 'describe' ? (
              <ModifyForm
                appId={data.id}
                slug={data.slug}
                currentVersionNumber={data.currentVersionNumber}
                currentComment={data.comment}
                onCancel={() => setUpdateOpen(false)}
              />
            ) : (
              <RedeployForm
                appId={data.id}
                slug={data.slug}
                domain={data.domain}
                url={data.url}
                currentVersionNumber={data.currentVersionNumber}
                currentComment={data.comment}
                versions={versions}
                currentVersionId={data.currentVersionId}
                onCancel={() => setUpdateOpen(false)}
              />
            )}
          </Card>
        ) : null}

        <Card>
          <SectionTitle hint="De la plus récente à la plus ancienne. Les tentatives échouées y figurent : c'est ce qui explique un numéro manquant.">
            Historique des versions
          </SectionTitle>
          <VersionHistory
            versions={versions}
            loading={versionsQuery.loading}
            error={versionsQuery.error}
            onReload={versionsQuery.reload}
            selectedVersionId={targetVersionId}
            onSelect={setSelectedVersionId}
            onRollback={(version) => {
              setRollbackError(null);
              setRollbackTarget(version);
            }}
          />
        </Card>

        <Card>
          <SectionTitle hint="Par défaut, la version sélectionnée est comparée à celle qui la précède.">
            Comparer deux versions
          </SectionTitle>
          <div className="p-4">
            <VersionDiffPanel
              appId={data.id}
              versions={versions}
              targetVersionId={targetVersionId}
              onTargetVersionChange={setSelectedVersionId}
            />
          </div>
        </Card>
      </div>

      <RollbackConfirmDialog
        version={rollbackTarget}
        slug={data.slug}
        nextNumber={nextVersionNumber(versions)}
        busy={rollbackBusy}
        error={rollbackError}
        onConfirm={confirmRollback}
        onCancel={() => setRollbackTarget(null)}
      />
    </>
  );
}
